// src/components/Header.jsx

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom'; // 1. Import Link, useLocation, and useNavigate
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useThemeManager } from '../hooks/useThemeManager';

const Header = () => {
    const [isSticky, setIsSticky] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { theme, toggleTheme } = useThemeManager(); 
    
    // 2. Get the current page's location and the navigate function
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const handleScroll = () => {
            setIsSticky(window.scrollY > 50);
            // Auto-close mobile menu when scrolling
            if (isMobileMenuOpen) {
                setIsMobileMenuOpen(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobileMenuOpen]);

    // Scroll to top when location changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    // 3. This function now handles smart scrolling for section links
    const handleSectionScroll = (id) => {
        setIsMobileMenuOpen(false);
        // If we are already on the homepage, just scroll smoothly
        if (location.pathname === '/') {
            const section = document.getElementById(id);
            if (section) {
                section.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            // If we are on another page (like the blog), navigate to the homepage
            // with the section ID in the hash. The browser will handle the scroll on load.
            navigate(`/#${id}`);
        }
    };

    // Handle projects page navigation
    const handleProjectsClick = (e) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        navigate('/projects');
        setTimeout(() => window.scrollTo(0, 0), 0);
    };

    // Handle blog navigation - always scroll to top
    const handleBlogClick = (e) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        navigate('/blog');
        // Ensure scroll to top after navigation
        setTimeout(() => window.scrollTo(0, 0), 0);
    };

    // Handle home/logo clicks - scroll to top if on homepage, navigate if not
    const handleHomeClick = (e) => {
        e.preventDefault();
        setIsMobileMenuOpen(false);
        if (location.pathname === '/') {
            // Already on homepage - scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Navigate to homepage
            navigate('/');
            // Ensure scroll to top after navigation
            setTimeout(() => window.scrollTo(0, 0), 0);
        }
    };
    
    // This helper just makes the link syntax cleaner
    const navLink = (id, label) => (
        <a 
          href={`/#${id}`} 
          onClick={(e) => {
            e.preventDefault();
            handleSectionScroll(id);
          }} 
          className="text-slate-700 dark:text-gray-300 hover:text-cyan-400 dark:hover:text-cyan-400 transition-colors duration-300 font-medium"
        >
            {label}
        </a>
    );

    return (
        <motion.header 
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={`fixed w-full z-50 transition-all duration-300 ease-in-out ${isSticky ? 'bg-white/65 dark:bg-black/75 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}
        >
            <div className="container mx-auto px-6 flex justify-between items-center py-6" style={{ minHeight: 80 }}>
                {/* 4. The logo now uses a <Link> tag to go to the homepage */}
                <Link to="/" onClick={handleHomeClick} className="flex items-center gap-2">
                    <img
                        src="/logo_smweb.png"
                        alt="SmartSolutions Web Logo"
                        className="h-16 w-auto object-contain"
                        style={{ maxHeight: 64 }}
                    />
                </Link>

                {/* 5. The main navigation is updated with Home and Blog links */}
                <nav className="hidden md:flex items-center space-x-8">
                    <Link to="/" onClick={handleHomeClick} className="text-slate-700 dark:text-gray-300 hover:text-cyan-400 dark:hover:text-cyan-400 transition-colors font-medium">Home</Link>
                    {navLink('about', 'About')}
                    {navLink('services', 'Services')}
                    <Link to="/projects" onClick={handleProjectsClick} className="text-slate-700 dark:text-gray-300 hover:text-cyan-400 dark:hover:text-cyan-400 transition-colors font-medium">Work</Link>
                    {navLink('contact', 'Contact')}
                    <Link to="/blog" onClick={handleBlogClick} className="text-slate-700 dark:text-gray-300 hover:text-cyan-400 dark:hover:text-cyan-400 transition-colors font-medium">Blog</Link>
                    
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <motion.button 
                        onClick={toggleTheme} 
                        className="p-2 rounded-full text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div key={theme} initial={{opacity:0, rotate: -90}} animate={{opacity:1, rotate:0}} exit={{opacity:0, rotate:90}} transition={{duration:0.2}}>
                                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                            </motion.div>
                        </AnimatePresence>
                    </motion.button>
                    <a href="#contact" onClick={(e) => { e.preventDefault(); handleSectionScroll('contact');}} className="px-6 py-2 bg-cyan-500 text-black rounded-full font-semibold hover:bg-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg">Get Started</a>
                </div>

                <div className="md:hidden flex items-center gap-2">
                    <motion.button 
                        onClick={toggleTheme} 
                        className="p-2 rounded-full text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    >
                        <AnimatePresence mode="wait">
                            <motion.div key={theme} initial={{opacity:0, rotate: -90}} animate={{opacity:1, rotate:0}} exit={{opacity:0, rotate:90}} transition={{duration:0.2}}>
                                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
                            </motion.div>
                        </AnimatePresence>
                    </motion.button>
                    <button 
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                        className="p-2 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        aria-label="Toggle menu"
                    >
                        <motion.div
                            animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </motion.div>
                    </button>
                </div>
            </div>

            {/* 6. The mobile menu is also updated */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                        className="md:hidden bg-white dark:bg-black/95 backdrop-blur-lg border-t border-gray-700 dark:border-gray-600 shadow-lg"
                    >
                        <nav className="flex flex-col space-y-1 p-4">
                            <Link 
                                to="/" 
                                onClick={handleHomeClick} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                Home
                            </Link>
                            <a 
                                href="/#about" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSectionScroll('about');
                                }} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                About
                            </a>
                            <a 
                                href="/#services" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSectionScroll('services');
                                }} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                Services
                            </a>
                            <Link 
                                to="/projects" 
                                onClick={handleProjectsClick} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                Work
                            </Link>
                            <a 
                                href="/#contact" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleSectionScroll('contact');
                                }} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                Contact
                            </a>
                            <Link 
                                to="/blog" 
                                onClick={handleBlogClick} 
                                className="px-4 py-3 text-black dark:text-gray-300 hover:text-cyan-400 hover:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium"
                            >
                                Blog
                            </Link>
                            
                            <div className="pt-4 mt-4 border-t border-gray-700 dark:border-gray-600">
                                <a 
                                    href="#contact" 
                                    onClick={(e) => { 
                                        e.preventDefault(); 
                                        handleSectionScroll('contact');
                                    }} 
                                    className="block w-full text-center px-6 py-3 bg-cyan-500 text-black rounded-lg font-semibold hover:bg-cyan-400 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                >
                                    Get Started
                                </a>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;